import { useMeasurementUnit } from 'hooks/useMeasurementUnit';
import { RevxMeasure } from 'components/RevxMeasure';
import { MeasureType } from 'domains/measurementDomain';
import { useMemoizedFn } from 'ahooks';
import { Radio, RadioChangeEvent, Tag } from 'antd';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { RevxButton } from 'components/RevxButton';
import RevxFlexLayout from 'components/RevxFlexLayout';
import RevxTable, { TableColumnsType } from 'components/RevxTable';
import {
  Parcel,
  ParcelCategoryDisplay,
  ParcelCategoryEnum,
  PartialParcel,
} from 'domains/parcelDomain';
import { useOrgId } from 'hooks/useOrg';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export const ParcelTable = () => {
  const { columnTitle } = useMeasurementUnit();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const loading = useAppSelector(
    (state) => state.loading.effects.parcel.getParcels
  );
  const pagedData = useAppSelector((state) => state.parcel.pagedData);
  const pageSize = useAppSelector((state) => state.parcel.pageSize);
  const currentPage = useAppSelector((state) => state.parcel.currentPage);
  const curItem = useAppSelector((state) => state.parcel.curItem);
  const baseSelectCurItem = useAppSelector(
    (state) => state.parcel.baseSelectState.curItem
  );
  const filterObj = useAppSelector((state) => state.parcel.filterObj);
  const orgId = useOrgId();
  const orgList = useAppSelector((state) => state.org.list);

  const boxChargeEditable = useMemo(() => {
    return Boolean(
      orgList.find((item) => item.id === orgId)?.boxChargeEditable
    );
  }, [orgList, orgId]);

  const boxChargeEditableHandle = useMemoizedFn(async (e: RadioChangeEvent) => {
    dispatch.app.showLoading();
    try {
      await dispatch.org.updateBoxChargeEditable({
        boxChargeEditable: Boolean(e.target.value),
        orgId,
      });
    } catch {
    } finally {
      dispatch.app.hideLoading();
    }
  });

  const columns: TableColumnsType<Parcel> = [
    {
      title: t('common.id'),
        width: 60,
      dataIndex: 'id',
    },
    {
      title: t('common.revxId'),
      dataIndex: 'revxId',
    },
    {
      title: t('common.name'),
      dataIndex: 'name',
    },
    {
      title: t('platform.parcel.columnTitle.category'),
      dataIndex: 'category',
      render: (value: ParcelCategoryEnum) => {
        return ParcelCategoryDisplay[value];
      },
    },
    {
      title: columnTitle(
        t('platform.parcel.columnTitle.width'),
        MeasureType.LENGTH
      ),
      dataIndex: 'widthIn',
      render: (value: number, record: Parcel) => (
        <RevxMeasure
            value={value}
            metric={record.widthMm}
            type={MeasureType.LENGTH}
            withSymbol={false}
          />
      ),    },
    {
      title: columnTitle(
        t('platform.parcel.columnTitle.height'),
        MeasureType.LENGTH
      ),
      dataIndex: 'heightIn',
      render: (value: number, record: Parcel) => (
        <RevxMeasure
            value={value}
            metric={record.heightMm}
            type={MeasureType.LENGTH}
            withSymbol={false}
          />
      ),    },
    {
      title: columnTitle(
        t('platform.parcel.columnTitle.length'),
        MeasureType.LENGTH
      ),
      dataIndex: 'lengthIn',
      render: (value: number, record: Parcel) => (
        <RevxMeasure
            value={value}
            metric={record.lengthMm}
            type={MeasureType.LENGTH}
            withSymbol={false}
          />
      ),    },
    {
      title: columnTitle(
        t('platform.parcel.columnTitle.weight'),
        MeasureType.WEIGHT
      ),
      dataIndex: 'weightLb',
      render: (value: number, record: Parcel) => (
        <RevxMeasure
            value={value}
            metric={record.weightKg}
            type={MeasureType.WEIGHT}
            withSymbol={false}
          />
      ),    },
    {
      title: t('platform.parcel.columnTitle.itemsPerParcel'),
      dataIndex: 'itemsPerParcel',
      responsive: ['xl'],
    },
    {
      title: t('platform.parcel.columnTitle.price'),
      dataIndex: 'price',
      valueType: 'money',
      responsive: ['xl'],
    },
    {
      title: t('platform.parcel.columnTitle.markupPct'),
      dataIndex: 'markupPct',
      valueType: 'percentage',
      responsive: ['xl'],
    },
    {
      title: t('common.active'),
      dataIndex: 'active',
      render: (active) => (
        <Tag color={active ? 'green' : 'volcano'}>
          {active ? t('common.active') : t('common.inactive')}
        </Tag>
      ),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      width: 100,
      render: (value, record) => (
        <RevxButton
          type="link"
          key="view"
          size="small"
          onClick={() => dispatch.parcel.setState({ curItem: record })}
        >
          {t('common.view')}
        </RevxButton>
      ),
    },
  ];

  const onTableChange = useCallback(
    (pagination, filters, sorter, extra) => {
      switch (extra.action) {
        case 'paginate':
          if (pagination.pageSize !== pageSize) {
            dispatch.parcel.setState({
              pageSize: pagination.pageSize,
              currentPage: 1,
            });
            dispatch.parcel.setState({ pageSize: pagination.pageSize });
          }

          if (pagination.current !== currentPage) {
            dispatch.parcel.setState({
              currentPage: pagination.current,
            });
          }
          break;
      }
    },
    [currentPage, dispatch.parcel, pageSize]
  );

  useEffect(() => {
    dispatch.parcel.getParcels({
      page: { currentPage, pageSize },
      query: { ...filterObj },
    });
  }, [currentPage, dispatch.parcel, filterObj, pageSize]);

  return (
    <>
      <RevxFlexLayout>
        <div style={{ paddingRight: 12 }}>
          Sourcer or supplier can edit box charge:
        </div>
        <Radio.Group
          value={boxChargeEditable}
          onChange={boxChargeEditableHandle}
        >
          <Radio value={true}>Yes</Radio>
          <Radio value={false}>No</Radio>
        </Radio.Group>
      </RevxFlexLayout>
      <div hidden={!!curItem || !!baseSelectCurItem}>
        <RevxTable<Parcel>
          loading={loading}
          columns={columns}
          size="small"
          pagination={{
            pageSize: pageSize,
            current: currentPage,
            total: pagedData?.totalElements,
            showTotal: (total) => t('common.pagerTotal', { total: total }),
          }}
          rowClassName={(record: PartialParcel) => {
            if (curItem?.id === record.id) {
              return 'editing';
            }
            return '';
          }}
          indentSize={20}
          dataSource={pagedData?.content || []}
          onChange={onTableChange}
        />
      </div>
    </>
  );
};
